import { Immutable } from "immer";
import React from "react";
import { DynamicPrice } from "tsi-common-react/src/components/DynamicPrice/index";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./charts.module.scss";

/**
 * Posturepedic comparison-chart ports of the deleted
 * ``cms/blocks/hc-block/hc-block--posturepedic-*.html`` partials (#33285).
 *
 * The static table markup is reproduced verbatim (classes / GTM hooks / copy
 * unchanged). Each chart's "Starting At" / "Price" row rendered a
 * ``{% include_react "...DynamicPriceTemplate" product=..._json %}`` island;
 * here those become ``<ChartPrice>`` -- the DynamicPrice component composed
 * directly (it is purely presentational). The per-slot product-API JSON is
 * resolved server-side by ``HardcodedBlock.get_data_for_client_render`` in the
 * same column order these components index.
 */

type IChartProps = Immutable<{
    products: readonly (string | null)[];
}>;

// One chart price cell. Reproduces the legacy static
// ``<div class="hc-block-price-container">`` wrapper (always rendered) with the
// DynamicPrice island inside it -- parsing the product-API JSON exactly as the
// DynamicPriceTemplate wrapper did. A null slot (product not found) renders the
// empty container, matching the legacy island's empty render.
const ChartPrice = (
    props: Immutable<{ product: string | null }>,
): React.JSX.Element => {
    const product = props.product
        ? (() => {
              try {
                  return JSON.parse(props.product) as IProduct;
              } catch {
                  return null;
              }
          })()
        : null;
    return (
        <div className="hc-block-price-container">
            {product && <DynamicPrice productPrice={product.price} />}
        </div>
    );
};

export const PosturepedicEliteHybrid = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className={styles.alignedHeaderSection}>
                    <div className="rich-text">
                        <h3>Personalized Sleep</h3>
                        <h1>Choose Your Comfort Level</h1>
                    </div>
                </section>
            </header>
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className={styles.mobileBreak} />
                                Technology
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerSpring,
                                            ])}
                                        >
                                            Good
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Albany II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerHybrid,
                                            ])}
                                        >
                                            Better
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Brenham II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerBest,
                                            ])}
                                        >
                                            Best
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>High Point II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className={styles.mobileBreak} />
                                Height
                            </th>
                            <td>13&quot;</td>
                            <td>14&quot;</td>
                            <td>14&quot;</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Cooling</th>
                            <td>Cooling Cover</td>
                            <td>Cooling Cover</td>
                            <td>Advanced Cooling Cover + Cooling Foam</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Performance{" "}
                                <br className={styles.mobileBreak} />
                                Foams
                            </th>
                            <td>2&quot; Gel Memory Foam Layer</td>
                            <td>2.5&quot; Gel Memory Foam Layer</td>
                            <td>Up to 3.5&quot; Gel Memory Foam Layer</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Available <br className={styles.mobileBreak} />
                                Feels
                            </th>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                className={concatClassNames([
                                    styles.mediumCol,
                                    styles.multipleFeels,
                                ])}
                            >
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.firm,
                                                ])}
                                            ></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.goastLine}></div>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.soft,
                                                ])}
                                            ></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                className={concatClassNames([
                                    styles.mediumCol,
                                    styles.multipleFeels,
                                ])}
                            >
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.firm,
                                                ])}
                                            ></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.soft,
                                                ])}
                                            ></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Starting At</th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[2] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const PosturepedicEliteSpring = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className={styles.alignedHeaderSection}>
                    <div className="rich-text">
                        <h3>Personalized Sleep</h3>
                        <h1>Choose Your Comfort Level</h1>
                    </div>
                </section>
            </header>
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className={styles.mobileBreak} />
                                Technology
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerSpring,
                                            ])}
                                        >
                                            Good
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Albany II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerHybrid,
                                            ])}
                                        >
                                            Better
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Brenham II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className={styles.mobileBreak} />
                                Height
                            </th>
                            <td>14&quot;–15&quot;</td>
                            <td>15&quot;–16&quot;</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Cooling</th>
                            <td>Cooling Cover</td>
                            <td>Cooling Cover</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Performance{" "}
                                <br className={styles.mobileBreak} />
                                Foams
                            </th>
                            <td>Up to 2.5&quot; Gel Foam Layer</td>
                            <td>Up to 3&quot; Gel Foam Layer</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Tight <br className={styles.mobileBreak} />
                                Top Feels
                            </th>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.firm,
                                                ])}
                                            ></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Pillowtop <br className={styles.mobileBreak} />
                                Feels
                            </th>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.soft,
                                                ])}
                                            ></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                className={concatClassNames([
                                    styles.mediumCol,
                                    styles.multipleFeels,
                                ])}
                            >
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.firm,
                                                ])}
                                            ></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.soft,
                                                ])}
                                            ></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Starting At</th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const PosturepedicSpringHybridStandard = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className={styles.alignedHeaderSection}>
                    <div className="rich-text">
                        <h4>
                            Discover the Sealy Posturepedic<sup>®</sup>{" "}
                            Collection
                        </h4>
                        <h1>Spring vs. Hybrid</h1>
                    </div>
                </section>
            </header>
            <div className={styles.tableContainer}>
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className={styles.mobileBreak} />
                                Technology
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerSpring,
                                            ])}
                                        >
                                            Posturepedic<sup>®</sup> Spring
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Ultra&nbsp;</span>
                                            <span>Responsive</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className={styles.supportTypes}>
                                <div className={styles.supportTypesContent}>
                                    <div className={styles.top}>
                                        <span
                                            className={concatClassNames([
                                                styles.supportBanner,
                                                styles.supportBannerHybrid,
                                            ])}
                                        >
                                            Posturepedic<sup>®</sup> Hybrid
                                        </span>
                                        <span className={styles.supportType}>
                                            <span>Premium&nbsp;</span>
                                            <span>Pressure Relief</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className={styles.mobileBreak} />
                                Height
                            </th>
                            <td>13”</td>
                            <td>12”</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Coils</th>
                            <td>
                                <span
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td>
                                <span
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Foam <br className={styles.mobileBreak} />
                                Layers
                            </th>
                            <td>
                                <span
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td>
                                <div
                                    className={concatClassNames([
                                        styles.twoRows,
                                        styles.rowWithCheckIcon,
                                    ])}
                                >
                                    <span
                                        className={styles.checkIcon}
                                        aria-hidden="true"
                                    ></span>
                                    <span className="ada-screenreader-only">
                                        {t`Yes`}
                                    </span>
                                    <span>2x more conforming foam</span>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Support</th>
                            <td>
                                <div className={styles.twoRows}>
                                    <span>
                                        <b>Center Support</b>
                                    </span>
                                    <span
                                        className={concatClassNames([
                                            styles.copyGap,
                                            styles.copyAlignment,
                                        ])}
                                    >
                                        <span>
                                            maintains natural body alignment
                                        </span>
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className={styles.twoRows}>
                                    <span className={styles.titleAlignment}>
                                        <span>
                                            <b>ComfortSense™</b>
                                        </span>
                                    </span>
                                    <span
                                        className={concatClassNames([
                                            styles.copyGap,
                                            styles.copyAlignment,
                                        ])}
                                    >
                                        <span>
                                            provides maximum conforming comfort
                                        </span>
                                    </span>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Available <br className={styles.mobileBreak} />
                                Feels
                            </th>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={styles.mediumCol}>
                                <div className={styles.availableFeelsContainer}>
                                    <div className={styles.wrapper}>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.medium,
                                                ])}
                                            ></span>
                                            <span>Medium</span>
                                        </div>
                                        <div className={styles.availableFeels}>
                                            <span
                                                className={concatClassNames([
                                                    styles.availableFeelsIcon,
                                                    styles.firm,
                                                ])}
                                            ></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Price <br className={styles.mobileBreak} />
                                (Queen)
                            </th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
